/* @flow */
import { combineEpics } from 'redux-observable';
import { FETCH_NEAREST_ASSET, FETCH_NEAREST_LANDMARK } from './constants.nearestAssetLandmark';
import { fetchNearestAssetLandmarkSuccess, fetchNearestAssetLandmarkError } from './actions.nearestAssetLandmark';
import config from '../../constants/Config';
import { getUserToken } from '../../helper-classes/utility-functions';
import { toRouteWaypoint } from '../../util/map_utils';

type ObservarblesTypes = {
    getJSON: Function,
    of: Function,
    getRoute: Function,
};

/**
 * Builds { origin, destination } Waypoints for getRoute, or null when
 * either endpoint is missing.
 */
const buildRouteEndpoints = (assetData: Object, landmarkData: Object) => {
    const origin = { lat: assetData.latitude, lng: assetData.longitude };
    const destination = { lat: landmarkData.markerLat, lng: landmarkData.markerLng };
    if ([origin.lat, origin.lng, destination.lat, destination.lng]
        .some(value => value === null || value === undefined || value === '')) return null;
    return { origin: toRouteWaypoint(origin), destination: toRouteWaypoint(destination) };
};

const toDirectionsDetails = (route: ?Object) => (route ? {
    localizedValues: route.localizedValues,
    originLocation: route.legs?.[0]?.startLocation,
    destinationLocation: route.legs?.[route.legs.length - 1]?.endLocation,
    steps: (route.legs || []).flatMap(leg => leg.steps || []),
} : {});


export const getShapeData = (data: Object) => {
    const type = (data.shape) ? data.shape.toLowerCase() : '';
    switch (type) {
    case 'rectangle': return {
        type: 'RECTANGLE', point1: { lat: data.box.first.y, lng: data.box.first.x }, point2: { lat: data.box.second.y, lng: data.box.second.x },
    };
    case 'circle': return {
        type: 'CIRCLE', radius: data.circle.radius.value || 400, lat: parseFloat(data.circle.center.y), lng: parseFloat(data.circle.center.x),
    };
    case 'polygon': return {
        type: 'POLYGON', points: data.polygon.points.map(d => ({ lat: d.y, lng: d.x })),
    };
    default: return '';
    }
};

const getSpeed = (data: any) => {
    if (data && data.status && data.status.toLowerCase() === 'stopped') {
        return 0;
    }
    return (data.speed) ? Math.round(data.speed) : '0';
};

export const formatAssetData = (data: Object) => ({
    assetId: data.id || '',
    assetName: data.name || '',
    status: data.status || '',
    deviceSerialNumber: data.deviceSerialNumber || '',
    locationLastReported: data.locationLastReported || '',
    statusStartDate: data.statusStartDate || '',
    statusDurationInSeconds: data.statusDurationInSeconds || '',
    statusDisplayDate: data.statusDisplayDate || '',
    lastEventDate: data.lastEventDate || '',
    reportingStatus: data.reportingStatus || '',
    lat: (data.lastLocation) ? data.lastLocation.y : 0,
    lng: (data.lastLocation) ? data.lastLocation.x : 0,
    latitude: (data.latitude) ? parseFloat(data.latitude) : 0,
    longitude: (data.longitude) ? parseFloat(data.longitude) : 0,
    address: `${data.address || ''}${(data.address) ? ', ' : ''}${data.city || ''}${(data.city) ? ', ' : ''}${data.state || ''} ${data.zipCode || ''}`,
    assetType: data.type || '',
    typeName: data.typeName || '',
    direction: data.direction || '',
    assetStatus: {
        heading: data.direction || '',
        speed: getSpeed(data),
        lastEvent: data.lastEvent || '',
        lastEventDate: data.lastEventDate || '',
    },
    assetDetails: {
        assetGroupName: data.assetGroupName || '',
        driver: data.driverName || '',
        tags: data.tags || '',
        details: '',
    },
    icon: `${(data.attributes && data.attributes.icon) ? data.attributes.icon : ''}`,
    labelColor: `${(data.attributes && data.attributes.labelColor) ? data.attributes.labelColor : ''}`,
});

const formatLandmarkData = data => ({
    id: data.id || '',
    name: data.name || '',
    asset: `${data.totalAssets} Assets` || '',
    address: `${data.address || ''}${(data.address) ? ', ' : ''}${data.city || ''}${(data.city) ? ', ' : ''}${data.state || ''} ${data.zipCode || ''}`,
    shape: getShapeData(data),
    markerLat: data.markerLat || 0,
    markerLng: data.markerLng || 0,
    icon: `${(data.attributes) ? (data.attributes.lfLandmarkIcon || data.attributes.att_0cm61 || '') : ''}`,
    attributes: data.attributes,
});

let landmarkDetails = {};
let directionsDetails = {};
let assetDetails = {};
export const getNearestLandmark =
    (actions$: Function, store: Object, { getJSON, of, getRoute }: ObservarblesTypes) =>
        actions$
            .ofType(FETCH_NEAREST_LANDMARK)
            .mergeMap((action) => {
                const headers = {
                    'X-Nspire-UserToken': getUserToken() || '',
                };
                landmarkDetails = {};
                directionsDetails = {};
                assetDetails = {};
                return getJSON(`${config.get('FLEET_VIEW_SERVICES_URL')}/assets/${action.payload.id}`, headers)
                    .mergeMap((assetResults) => {
                        if (assetResults.count > 0) {
                            assetDetails = formatAssetData(assetResults.data[0]);
                        }
                        return getJSON(`${config.get('FLEET_VIEW_SERVICES_URL')}/landmarks/nearest?lat=${assetDetails.lat}&lng=${assetDetails.lng}`, headers)
                            .mergeMap((nearestlandmarkresult) => {
                                landmarkDetails = nearestlandmarkresult.count > 0
                                    ? nearestlandmarkresult.data[0]
                                    : {};
                                landmarkDetails = formatLandmarkData(landmarkDetails);
                                const endpoints = buildRouteEndpoints(
                                    assetDetails,
                                    landmarkDetails,
                                );
                                return endpoints ? getRoute(endpoints) : of(null);
                            });
                    }).mergeMap((route) => {
                        directionsDetails = toDirectionsDetails(route);
                        return of(fetchNearestAssetLandmarkSuccess(
                            route || {},
                            landmarkDetails,
                            assetDetails,
                            directionsDetails,
                        ));
                    }).catch(error => of(fetchNearestAssetLandmarkError(error)));
            });

export const getNearestAsset =
    (actions$: Function, store: Object, { getJSON, of, getRoute }: ObservarblesTypes) =>
        actions$
            .ofType(FETCH_NEAREST_ASSET)
            .mergeMap((action) => {
                const headers = {
                    'X-Nspire-UserToken': getUserToken() || '',
                };
                landmarkDetails = {};
                directionsDetails = {};
                assetDetails = {};
                return getJSON(`${config.get('FLEET_VIEW_SERVICES_URL')}/landmarks/${action.payload.id}`, headers)
                    .mergeMap((landmarkResults) => {
                        if (landmarkResults.count > 0) {
                            landmarkDetails = formatLandmarkData(landmarkResults.data[0]);
                        }
                        return getJSON(`${config.get('FLEET_VIEW_SERVICES_URL')}/assets/nearest?lat=${landmarkDetails.markerLat}&lng=${landmarkDetails.markerLng}`, headers)
                            .mergeMap((nearestassetresult) => {
                                assetDetails = nearestassetresult.count > 0
                                    ? nearestassetresult.data[0]
                                    : {};
                                assetDetails = formatAssetData(assetDetails);
                                // Directions are resolved here via the Routes API v2
                                const endpoints = buildRouteEndpoints(
                                    assetDetails,
                                    landmarkDetails,
                                );
                                return endpoints ? getRoute(endpoints) : of(null);
                            });
                    }).mergeMap((route) => {
                        directionsDetails = toDirectionsDetails(route);
                        return of(fetchNearestAssetLandmarkSuccess(
                            route || {},
                            landmarkDetails,
                            assetDetails,
                            directionsDetails,
                        ));
                    }).catch(error => of(fetchNearestAssetLandmarkError(error)));
            });

export default combineEpics(getNearestLandmark, getNearestAsset);
